import React, { useState } from "react";
import { toast } from "sonner";
import { api, isSessionExpired } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";

/**
 * Cancel-confirmation dialog. Preserves optimistic-concurrency semantics via
 * `if_version` and treats 409 as a conflict (banner + refresh signal). The
 * "job_not_terminal" backend detail races with a worker completion — treat as
 * a benign refresh trigger.
 */
export function CancelConfirmDialog({
  job,
  open,
  onClose,
  onCancelled,
}: {
  job: { id: string; version: number };
  open: boolean;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [inFlight, setInFlight] = useState(false);
  const [conflict, setConflict] = useState(false);

  const doCancel = async () => {
    if (inFlight) return;
    setInFlight(true);
    setConflict(false);
    try {
      await api.post(`/import/jobs/${job.id}/cancel`, { if_version: job.version });
      toast.success("Cancellation requested");
      onCancelled();
      onClose();
    } catch (e: unknown) {
      if (isSessionExpired(e)) return;
      const s = (e as { response?: { status?: number } })?.response?.status;
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail;
      if (s === 409) {
        setConflict(true);
        onCancelled();
      } else if (typeof detail === "string" && detail.startsWith("job_not_terminal")) {
        // Race with worker completing — refresh to the terminal summary.
        onCancelled();
        onClose();
      } else {
        toast.error("Could not cancel the import.");
      }
    } finally {
      setInFlight(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="cancel-confirm-dialog" aria-describedby="cancel-desc">
        <DialogHeader>
          <DialogTitle>Cancel this import?</DialogTitle>
          <DialogDescription id="cancel-desc">
            The worker will safely finish the currently claimed row and then stop.
            Already-committed contact changes will be preserved.
          </DialogDescription>
        </DialogHeader>
        {conflict && (
          <div
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="cancel-conflict-banner"
          >
            Job state changed. Refresh and try again.
          </div>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            className="rounded-full"
            onClick={onClose}
            disabled={inFlight}
            data-testid="cancel-confirm-cancel-btn"
          >
            Keep running
          </Button>
          <Button
            className="rounded-full"
            onClick={doCancel}
            disabled={inFlight}
            data-testid="cancel-confirm-go-btn"
          >
            {inFlight ? "Requesting…" : "Yes, request cancellation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
